import { ZButton } from "@/shared/components/button";
import { ZInput } from "@/shared/components/input";
import KeyIcon from "@/shared/icons/key-icon";
import GoogleIcon from "@/shared/images/google_logo.png";
import MicrosoftIcon from "@/shared/images/logos_microsoft-icon.svg";
import { addNotification } from "@/shared/states/notification";
import parsePromise from "@/shared/utils/parsePromise";
import { ArrowLeft, ArrowRightIcon, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from '@tanstack/react-query'
import {
  setLoginStep,
  useDiscoveredOrganizations,
  useLoginStep
} from "../../states/login";

import { authChannel } from "@/infra/auth/broadcast";
import { generateJWTTokenFromInitialAuth, generateMagicLinks, getMemberDetailsWithOrganization, getSettingsApi, getTeam, revokeToken, switchOrganization } from "@/modules/auth/requests";
import { setTeam } from "@/modules/team/states";
import ZaniaLogoLoader from "@/shared/components/page-loader";
import { ROUTES } from "@/shared/constants/routes";
import { useUserStateSelector } from "@/shared/states/user";
import { cn } from "@/shared/utils/classname-merger";
import { deleteCookie, getCookie, setCookie } from "@/shared/utils/cookie-helper";
import { Tooltip } from "@nextui-org/react";
import { setAuth, useLoggedInMember } from "../../states";
import { LOGIN_STEP } from "../../types";
import { signInWithGoogleOauth } from "../../use-cases/google.use-case";
import { signInWithMicrosoftOauth } from "../../use-cases/microsoft.use-case";
import { getListOfOrganizationBasedOnEmail, startSsoConnection } from "../../use-cases/sso.use-case";
import AuthLayout from "../layout";
import LoginCard from "../login-card";
import { EMAIL_REGEX } from "../../../../shared/constants";
const Login = () => {
  const loginStep = useLoginStep();
  const [loading, setLoading] = useState(false);
  const [orgSelectLoading, setOrgSelectLoading] = useState(false);

  const [signingInWithGoogle, setSigningInWithGoogle] = useState(false);
  const [signingInWithMicrosoft, setSigningInWithMicrosoft] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const discoveredOrganizations = useDiscoveredOrganizations();
  const navigate = useNavigate();
  const location = useLocation();
  const { identifier } = useParams<{ identifier?: string }>();
  const isBusinessOwnerLogin = !!identifier;
  const setAIResponseType = useUserStateSelector.useSetAIResponseType();
  const setUserResponseType = useUserStateSelector.useSetUserResponseTypes();
  const emailRegex = EMAIL_REGEX;

  // if user is already logged in redirect to dashboard
  const loggedInMember = useLoggedInMember();
  useEffect(() => {
    if (!isBusinessOwnerLogin && location.pathname === '/login' && loggedInMember?.member_id) {
      window.location.href=ROUTES.AGENT;
    }
  }, []);

  useEffect(() => {
    // Handle user returning from OAuth flow
    const resetOAuthLoadingStates = () => {
      // Check if we have pending auth states and reset them
      if (signingInWithGoogle || signingInWithMicrosoft) {
        setSigningInWithGoogle(false);
        setSigningInWithMicrosoft(false);
        setLoading(false);
      }
    };

    // Add multiple event listeners to catch different scenarios
    window.addEventListener('pageshow', (event) => {
      // This catches bfcache navigation events (back/forward)
      if (event.persisted) {
        resetOAuthLoadingStates();
      }
    });
    
    window.addEventListener('popstate', resetOAuthLoadingStates);
    window.addEventListener('focus', resetOAuthLoadingStates);
    
    // Clean up
    return () => {
      window.removeEventListener('pageshow', resetOAuthLoadingStates);
      window.removeEventListener('popstate', resetOAuthLoadingStates);
      window.removeEventListener('focus', resetOAuthLoadingStates);
    };
  }, [signingInWithGoogle, signingInWithMicrosoft]);

  const onClickSendMagicLink = async () => {
    deleteCookie("sso_auth_method");
    // validate email
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    try {
      setLoading(true);
      const redirectUrl = isBusinessOwnerLogin
        ? window.location.href
        : `${window.location.origin}/auth`;
      await generateMagicLinks(email, redirectUrl, isBusinessOwnerLogin || undefined);
      setLoginStep(LOGIN_STEP.EMAIL_SUCCESS);
    } catch (e) {
      setError("An error occurred please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailOnChange = (newEmail: string) => {
    // Clear error if field is empty
    if (!newEmail.trim()) {
      setError("");
      return;
    }
    
    // Only show/maintain errors if there was already an error
    if (error) {
      if (emailRegex.test(newEmail)) {
        setError("");
      } else {
        setError("Please enter a valid email address.");
      }
    }
  }

  const onClickSSOLogin = async () => {
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    const [error, data] = await parsePromise<string[]>(
      getListOfOrganizationBasedOnEmail(email),
    );
    setLoading(false);
    if (data && data.length > 0) {
      const [error, resp] = await parsePromise<any>(
        startSsoConnection(data[0]),
      );
    } else {
      setError("There is no SAML SSO configured for this user");
    }

    if (error) {
      setError(error.message);
      addNotification({
        type: "error",
        message: "Failed to get List Of Organization",
      });
    }
  };

  const onClickGoogleLogin = async () => {
    deleteCookie("sso_auth_method");
    try {
      setSigningInWithGoogle(true);
      await signInWithGoogleOauth();
    } catch (e) {
      setError("An error occurred please try again.");
      setSigningInWithGoogle(false)
    }
  };

  const onClickMicrosoftLogin = async () => {
    deleteCookie("sso_auth_method");
    try {
      setSigningInWithMicrosoft(true);
      await signInWithMicrosoftOauth();
    } catch (e) {
      setError("An error occurred please try again.");
      console.error(e);
      setSigningInWithMicrosoft(false);
    }
  };

  const queryClient = useQueryClient();

  const handleOrgSelect = async (connection_id: string) => {
    const authType = getCookie("sso_auth_method");

    queryClient.clear()
    // if user is already logged in and is switching organization
    if (location.pathname === "/select-organization" || authType === 'sso') {
      setOrgSelectLoading(true);
      // Don't delete sso_auth_method cookie - we need it to track auth method
      const [error, resp] = await parsePromise<any>(
        switchOrganization(connection_id),
      );
      if (error) {
        addNotification({
          type: "error",
          title: "Failed to switch organization",
          message: error.message || "Something went wrong, please contact support@zania.ai",
        });
        setOrgSelectLoading(false);
        return;

      } else {
        authChannel.postMessage("switchedOrganization")
      }

      const [errorObj, member] = await parsePromise(getMemberDetailsWithOrganization());
      if (member) {
        setCookie("intermediate_session_token", "true", 1);
        setAuth({
          isAuthenticated: true,
          isAuthenticationInProgress: false,
          loggedInMember: member?.member,
          loggedInOrganization: member?.organization,
        });
        setLoginStep(LOGIN_STEP.MAIN);
        setOrgSelectLoading(false);
        const [err, settings] = await parsePromise(getSettingsApi());
        if (settings && settings.data && settings.data.ui_response_quality) {
          setAIResponseType(settings.data.ui_response_quality);
          setUserResponseType(settings.data.user_response_quality)
        }
        const [error, members] = await parsePromise(getTeam());
        if (members && members.team_members) {
          setTeam(members.team_members);
        }
        return;
      } else {
        setOrgSelectLoading(false);
      }

    }
    // if user is not logged in and is selecting organization
    else {
      deleteCookie("jwt_token");
      setOrgSelectLoading(true);
      const [error, resp] = await parsePromise(generateJWTTokenFromInitialAuth(connection_id));
      
      if(!error) {
        setAuth({
          isAuthenticated: true,
          isAuthenticationInProgress: false,
        });
        setTimeout(() => {
          setOrgSelectLoading(false);
          authChannel.postMessage("loggedIn");
          setLoginStep(LOGIN_STEP.MAIN);
        }, 110);
        
      }
      else {
        authChannel.postMessage("loggedOut");
        setLoginStep(LOGIN_STEP.MAIN);
        navigate(`/${ROUTES.LOGIN}`);
        setOrgSelectLoading(false);
      }
    }
    
  }

  const handleBackNavigation = () => {
    setLoginStep(LOGIN_STEP.MAIN);
    if (location.pathname === "/select-organization") {
      navigate(-1)
    } else {
      authChannel.postMessage("loggedOut")
      deleteCookie("intermediate_session_token")
    }
  }

  const renderLoginStep = () => {
    switch (loginStep) {
      case LOGIN_STEP.MAIN:
        return (
          <LoginCard
            title="Get started"
            description={`We'll sign you in or create an account if you don't have one
                yet.`}
          >
            <div className="grid gap-2">
              <ZInput
                id="email"
                type="email"
                label="Work Email"
                placeholder="johndoe@email.com"
                required
                value={email}
                isInvalid={!!error}
                errorMessage={error}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setEmail(newValue);
                  handleEmailOnChange(newValue);
                }}
                variant="faded"
              />
            </div>
            <ZButton
              className="w-full border-small"
              onClick={() => void onClickSendMagicLink()}
              isDisabled={signingInWithGoogle || loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Please Wait
                </>
              )  : "Send Magic Link"}
            </ZButton>
            {!isBusinessOwnerLogin && (
              <>
                <div className="flex items-center ">
                  <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                  <span className="mx-4 text-gray-500 dark:text-gray-400">or</span>
                  <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                </div>
                <div className="space-y-4">
                  <ZButton
                    className="w-full border-small p-4"
                    variant="bordered"
                    onClick={() => void onClickGoogleLogin()}
                    isDisabled={signingInWithGoogle || loading}
                    {...(!signingInWithGoogle && {
                      startContent: (
                        <img
                          src={GoogleIcon}
                          alt="Google"
                          className="h-5 w-5 mr-2"
                          loading="lazy"
                          decoding="async"
                        />
                      ),
                    })}
                  >
                    {signingInWithGoogle ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Please Wait
                    </>
                  )  : "Continue with Google"}
                  </ZButton>
                  <ZButton
                    className="w-full border-small p-4"
                    variant="bordered"
                    onClick={() => void onClickMicrosoftLogin()}
                    isDisabled={signingInWithMicrosoft || loading}
                    {...(!signingInWithMicrosoft && {
                      startContent: (
                        <img
                          src={MicrosoftIcon}
                          alt="Google"
                          className="h-5 w-5 mr-2"
                          loading="lazy"
                          decoding="async"
                        />
                      ),
                    })}
                  >
                    {signingInWithMicrosoft ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Please Wait
                    </>
                  )  :  "Continue with Microsoft"}
                  </ZButton>
                  <ZButton
                    className="w-full border-small p-4"
                    variant="bordered"
                    onClick={() => setLoginStep(LOGIN_STEP.SSO)}
                    {...(!signingInWithMicrosoft && {
                      startContent: <KeyIcon />,
                    })}
                  >
                    {"Continue with SAML SSO"}
                  </ZButton>
                </div>
              </>
            )}
          </LoginCard>
        );
      case LOGIN_STEP.EMAIL_SUCCESS:
        return (
          <LoginCard
            hideHeader
            title="Email Sent!"
            description="A magic link has been sent to your email. Click the link to login to your account."
          >
            <ZButton
              className="w-full"
              onClick={() => void onClickSendMagicLink()}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Please Wait
                </>
              )  : "Resend Magic Link"}
            </ZButton>
          </LoginCard>
        );
      case LOGIN_STEP.ERROR:
        return (
          <LoginCard
            title="Uh oh!"
            description="There was a error in logging you in. Please try again."
          >
            <ZButton
              className="w-full"
              onClick={() => setLoginStep(LOGIN_STEP.MAIN)}
            >
              Try Again
            </ZButton>
          </LoginCard>
        );
      case LOGIN_STEP.CONTACT_ADMIN:
        return (
          <LoginCard
            title="Contact Admin"
            description=" It seems that this organization already exists. Please contact the
              admin to get access."
          >
            <div className="flex items-center">
              <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
              <span className="mx-4 text-gray-500 dark:text-gray-400">or</span>
              <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
            </div>
          </LoginCard>
        );
      case LOGIN_STEP.ACCESS_RESTRICTED:
        return (
          <LoginCard
            title="Access Restricted!"
            description={
              'Contact <a href="mailto:sales@zania.ai" class="text-[#006FEE]">sales@zania.ai</a> to enable access to your organization.'
            }
          >
            <ZButton
              className="w-full"
              onClick={async () => {
                await revokeToken();
                setLoginStep(LOGIN_STEP.MAIN);
              }}
            >
              Back to Login
            </ZButton>
          </LoginCard>
        );
      case LOGIN_STEP.SELECT_ORG:
        return (
          <LoginCard hideHeader title={
            <div className="flex gap-3">
              <div
                className="cursor-pointer"
                onClick={() => handleBackNavigation()}
              >
                <ArrowLeft />
              </div>
              <div className="font-semibold text-lg">
                Select Organization
              </div>
            </div>
          }>
            <div className="max-h-[640px] grid gap-6 overflow-auto">

              {discoveredOrganizations?.map((organization) => {
                return (
                    <div
                    key={organization.organization.organization_id}
                      onClick={() => {
                      if (!loading)
                          handleOrgSelect(organization.organization.organization_id);
                      }}
                      className={cn(
                        "flex w-full border-[1px] cursor-pointer border-border border-[#006FEE] text-[#006FEE] items-center justify-between rounded-lg bg-white  p-4 py-2 shadow-sm dark:bg-gray-950",
                      )}
                    >
                      <div className="flex items-center ">
                        <div className="flex-shrink-0"></div>
                        <div>
                          <h3 className="text-base font-small">
                            {organization.organization.organization_name}
                          </h3>
                        </div>
                      </div>
                      <div>
                        <ArrowRightIcon
                          className={cn(
                            "h-6 w-6 text-[#006FEE] dark:text-[#006FEE]",
                          )}
                        />
                      </div>
                  </div>
                );
              })}
            </div>

          </LoginCard>
        );
      case LOGIN_STEP.SSO: {
        return (
          <LoginCard
            title={
              <div className="flex gap-3">
                <div
                  className="cursor-pointer"
                  onClick={() => {
                    setLoginStep(LOGIN_STEP.MAIN);
                    setEmail('');
                    setError('');
                  }}
                >
                  <ArrowLeft />
                </div>
                <div className="font-semibold text-lg">
                  Continue with SAML SSO
                </div>
              </div>
            }
          // description="Enter your company's email domain to authenticate using SSO"
          >
            <div className="grid gap-2">
              <div className="grid gap-2">
                <ZInput
                  id="email"
                  type="email"
                  label="Work Email"
                  placeholder="johndoe@email.com"
                  required
                  value={email}
                  isInvalid={!!error}
                  errorMessage={error}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setEmail(newValue);
                    handleEmailOnChange(newValue);
                  }}
                  variant="faded"
                />
              </div>
            </div>
            <ZButton
              className="w-full"
              onClick={() => void onClickSSOLogin()}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Please Wait
                </>
              ) : (
                "Sign in with SAML"
              )}
            </ZButton>
          </LoginCard>
        );
      }
    }
  };

  return <AuthLayout>{orgSelectLoading ? <ZaniaLogoLoader /> : renderLoginStep()}</AuthLayout>;
};

export default Login;
